import {
    type ProfileTasksUpdateTransport,
    serializeProfileTaskDraftReference,
} from '../api/profile-task-transport-adapter';
import type { ProfileTaskDraftReference, ProfileTaskParentContext, ProfileTaskSummary } from '../model/profile-task';
import type { ProfileTaskDraftCoordinator } from './profile-task-draft-coordinator';

export type PreparedProfileTaskSave =
    | { readonly status: 'absent' }
    | { readonly blockedTaskIds: readonly string[]; readonly status: 'blocked' }
    | {
          readonly affectedTaskCount: number;
          readonly ordered: readonly ProfileTaskDraftReference[];
          readonly removedTaskIds: readonly string[];
          readonly status: 'ready';
          readonly tasks: ProfileTasksUpdateTransport;
      };

export const prepareProfileTaskSave = (
    coordinator: ProfileTaskDraftCoordinator,
    context: ProfileTaskParentContext,
): PreparedProfileTaskSave => {
    if (coordinator.getState().getParent(context) === undefined) {
        return { status: 'absent' };
    }
    const prepared = coordinator.getState().prepareSave(context);
    if (!prepared.ok) {
        return { blockedTaskIds: prepared.blockedTaskIds, status: 'blocked' };
    }
    return {
        affectedTaskCount: prepared.ordered.filter(({ draft }) => draft !== undefined).length,
        ordered: prepared.ordered,
        removedTaskIds: prepared.removedTaskIds,
        status: 'ready',
        tasks: prepared.ordered.map(serializeProfileTaskDraftReference),
    };
};

export const preserveProfileTaskSaveFailure = (
    coordinator: ProfileTaskDraftCoordinator,
    context: ProfileTaskParentContext,
    prepared: PreparedProfileTaskSave,
    completedRemovalIds: readonly string[] = [],
): void => {
    if (prepared.status === 'ready') {
        coordinator.getState().saveFailed(context, completedRemovalIds);
    }
};

export const commitProfileTaskSave = (
    coordinator: ProfileTaskDraftCoordinator,
    context: ProfileTaskParentContext,
    parentVersion: number,
    canonicalTasks: readonly ProfileTaskSummary[],
): void => coordinator.getState().saveSucceeded(context, parentVersion, canonicalTasks);

/**
 * The backend rejects a tasks block that does not list exactly the tasks referenced by the item's
 * latest version, and task generation repins the item outside the edit session. The block is
 * therefore reconciled against the latest refs right before saving: tasks that no longer exist are
 * dropped, and tasks the session never saw are appended order-only.
 */
export const reconcileTasksBlockWithLatestRefs = (
    tasks: ProfileTasksUpdateTransport,
    latestTaskIds: readonly string[],
): ProfileTasksUpdateTransport => {
    const latest = new Set(latestTaskIds);
    const known = new Set(tasks.map(({ taskId }) => taskId));

    return [
        ...tasks.filter(({ taskId }) => latest.has(taskId)),
        ...latestTaskIds.filter((taskId) => !known.has(taskId)).map((taskId) => ({ taskId })),
    ];
};
